import { gameDefinitions, SupportedGames } from "@repo/game-configuration"
import { createFileRoute } from "@tanstack/react-router"
import { ChevronDown, CircleX } from "lucide-react"
import * as React from "react"
import { DataTable } from "@/components/data-table/data-table"
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header"
import type { DataTableColumnDef } from "@/components/data-table/data-table-features"
import { PageHeader } from "@/components/layout"
import { TaskIndicator } from "@/components/locks/task-indicator"
import { ImportGameModal } from "@/components/tasks/import-game-modal"
import { ImportSealedModal } from "@/components/tasks/import-sealed-modal"
import { TaskStatusCell } from "@/components/tasks/table/tasks-status-cell"
import {
	TASK_REGISTRY,
	type TaskItemProps,
} from "@/components/tasks/task-registry"
import { Button } from "@/components/ui/button"
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useStagingGames } from "@/queries/games/use-staging-games"
import { useLockStatus } from "@/queries/locks/use-lock-status"
import { formatGameName } from "@/utils/game.helpers"

export const Route = createFileRoute("/tasks/")({
	component: TasksPage,
	validateSearch: (search: Record<string, unknown>) => {
		return {
			search: search.search as string | undefined,
			...(typeof search.game === "string" ? { game: search.game } : {}),
			...(search.task === "import-game" || search.task === "import-sealed"
				? { task: search.task }
				: {}),
		}
	},
})

type Task = {
	name: string
	logo: string
	slug: string
	progress?: {
		stage: string
		currentStep: number
		totalSteps: number
		percentage: number
	}
}

const EXCLUDED_GAMES = [SupportedGames.Wankul, SupportedGames.ExampleGame]

function TasksPage() {
	const { game, task } = Route.useSearch()
	const navigate = Route.useNavigate()
	const [selectedGame, setSelectedGame] = React.useState<
		SupportedGames | undefined
	>(undefined)
	const [importGameModalVisible, toggleImportGameModalVisibility] =
		React.useState(false)
	const [importSealedModalVisible, toggleImportSealedModalVisibility] =
		React.useState(false)

	const { games: stagingGames } = useStagingGames({ page: 1, limit: 100 })

	React.useEffect(() => {
		if (!game || !task) return
		const selected = game as SupportedGames
		if (
			Object.values(SupportedGames).includes(selected) &&
			!EXCLUDED_GAMES.includes(selected)
		) {
			setSelectedGame(selected)
			if (task === "import-game") {
				toggleImportGameModalVisibility(true)
			} else {
				toggleImportSealedModalVisibility(true)
			}
		}
		navigate({
			search: (prev) => ({ ...prev, game: undefined, task: undefined }),
			replace: true,
		})
	}, [game, task, navigate])

	const stagingGameSlugs = React.useMemo(
		() => new Set(stagingGames.map((game) => game.slug)),
		[stagingGames],
	)

	const columns = React.useMemo<DataTableColumnDef<Task>[]>(
		() => [
			{
				accessorKey: "logo",
				enableSorting: false,
				header: () => null,
				cell: ({ row }) => {
					const logo = row.getValue("logo") as string
					const logoUrl = logo
						? logo.startsWith("http")
							? logo
							: `https://cardnexus.com${logo.startsWith("/") ? "" : "/"}${logo}`
						: null

					return logoUrl ? (
						<img
							src={logoUrl}
							alt={logo}
							className="h-5 w-10 object-contain"
							loading="lazy"
						/>
					) : (
						<CircleX className="size-3.5 text-meta" strokeWidth={2} />
					)
				},
			},
			{
				accessorKey: "name",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Game" />
				),
				cell: ({ row }) => {
					const name = row.getValue("name") as string | undefined
					const slug = row.original.slug
					const displayName = name || formatGameName(slug)

					return (
						<span className="block max-w-[180px] truncate font-medium">
							{displayName}
						</span>
					)
				},
			},
			{
				accessorKey: "operation",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Status" />
				),
				cell: ({ row }) => {
					const slug = row.original.slug as string

					return <TaskStatusCell slug={slug} />
				},
			},
			{
				id: "slug",
				cell: ({ row }) => {
					const slug = row.original.slug as string
					const hasGameInStaging = stagingGameSlugs.has(slug)

					return (
						<TaskActionsCellWithLock
							slug={slug}
							hasGameInStaging={hasGameInStaging}
							onRunImportGame={() => {
								setSelectedGame(slug as SupportedGames)
								toggleImportGameModalVisibility(true)
							}}
							onRunImportSealed={() => {
								setSelectedGame(slug as SupportedGames)
								toggleImportSealedModalVisibility(true)
							}}
						/>
					)
				},
			},
		],
		[stagingGameSlugs],
	)

	const rows = React.useMemo(
		() =>
			Object.values(gameDefinitions).filter(
				(game) => !EXCLUDED_GAMES.includes(game.slug as SupportedGames),
			),
		[],
	)

	return (
		<div className="flex h-[calc(100vh-3rem)] flex-col gap-6 p-6">
			<PageHeader title="Tasks" count={rows.length} />
			<div className="min-h-0 flex-1">
				<DataTable
					columns={columns}
					data={rows}
					tableId="tasks"
					facetedFilters={[]}
				/>
			</div>

			{selectedGame && (
				<>
					<ImportGameModal
						visible={importGameModalVisible}
						toggleEvent={toggleImportGameModalVisibility}
						gameSlug={selectedGame}
					/>
					<ImportSealedModal
						visible={importSealedModalVisible}
						toggleEvent={toggleImportSealedModalVisibility}
						gameSlug={selectedGame}
					/>
				</>
			)}
		</div>
	)
}

type TaskActionsCellWithLockProps = {
	slug: string
	hasGameInStaging: boolean
	onRunImportGame: () => void
	onRunImportSealed: () => void
}

function TaskActionsCellWithLock({
	slug,
	hasGameInStaging,
	onRunImportGame,
	onRunImportSealed,
}: TaskActionsCellWithLockProps) {
	const { isLocked, lockedBy, lockedAt, operation } = useLockStatus({
		gameSlug: slug,
	})

	if (isLocked) {
		return (
			<div className="flex flex-row justify-end">
				<TaskIndicator
					lockedAt={lockedAt ? new Date(lockedAt) : undefined}
					lockedBy={lockedBy}
					operation={operation}
					gameSlug={slug}
				/>
			</div>
		)
	}

	const itemProps: TaskItemProps = {
		slug: slug as SupportedGames,
		hasGameInStaging,
		openImportGameModal: onRunImportGame,
		openImportSealedModal: onRunImportSealed,
	}

	const tasks = TASK_REGISTRY.filter((task) => task.isAvailable(itemProps))

	return (
		<div className="flex flex-row justify-end">
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button variant="outline" size="sm">
						Run task
						<ChevronDown />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end" className="w-52">
					{tasks.map((task) => (
						<task.Item key={task.id} {...itemProps} />
					))}
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	)
}
